import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { OptionDataValuePrint } from '../../../shared/options';
import { RegistrationOptionInput } from '../../../shared/registration/model';
import { UserRegistration } from '../../../shared/user/base';
import {
  CashierCheck,
  CashierError,
  CashierStatus,
  CashierWarning,
  UndeliveredProducts,
} from '../../../shared/user/cashier';
import { ActionButton, MaterialIcon, RichMarkdownIt } from '../../components';
import { FulfillmentList } from '../../modules/order/fulfillment/FulfillmentList';
import { TranslationKey, useTranslation } from '../../translations';
import { displayName, isLogicError, useConvention } from '../../utils';
import { LogicError } from '../../utils/errorHandling';
import { CashierContext, getReceiptPrinter } from './cashiercontext';
import { CashierMessages, NotesList } from './messages';
import { NfcPrinterButton } from './nfc/NfcPrintButton';
import { OSPrinterButton } from './osprint/OSPrintButton';
import { ReceiptPrinterBase } from './printers/PrinterBase';

enum CashierStatusColor {
  danger = 'danger',
  warning = 'warning',
  success = 'success',
}

const statusLookup: Record<
  CashierStatus,
  {
    color: CashierStatusColor;
    icon: string;
    text: TranslationKey;
    explanation?: TranslationKey;
  }
> = {
  error: {
    color: CashierStatusColor.danger,
    icon: 'error_outline',
    text: 'checkin_blocked',
    explanation: 'one_or_more_errors_must',
  },
  warning: {
    color: CashierStatusColor.warning,
    icon: 'warning',
    text: 'checkin_warning',
    explanation: 'one_or_more_concerns_may',
  },
  complete: {
    color: CashierStatusColor.success,
    icon: 'check_circle_outline',
    text: 'checkin_complete',
  },
  ok: {
    color: CashierStatusColor.success,
    icon: 'radio_button_unchecked',
    text: 'checkin_ok',
    explanation: 'no_concerns_or_issues_were',
  },
};

interface AttendeeStatusProps {
  readonly profile: CashierCheck;
  readonly reload: () => void;
  readonly openUpdateFromScannerModal?: () => void;
}

export const AttendeeStatus: FC<AttendeeStatusProps> = ({
  profile,
  reload,
  openUpdateFromScannerModal,
}) => {
  const { ts, currency } = useTranslation();
  const cashierSettings = useContext(CashierContext);
  const { longName, venue } = useConvention();
  const { color, icon, text, explanation } = statusLookup[profile.status];

  const receiptAndReload = useCallback(() => {
    const printer = getReceiptPrinter(cashierSettings);
    if (printer === undefined) {
      reload();
      return;
    }

    // TODO make this dynamic and move it someplace better.

    // ReceiptLine formatted document
    const receipt = `
^^^CASH PAYMENT
${longName}
${venue}
{b:line}
Level | ${profile.registration?.attendanceType.displayName ?? 'n/a'}
Payment | ${currency(profile.dueBalance)}
Reg ID | ${profile.user.id}
-
Your registered email is
${profile.user.email ?? 'n/a'}
{b:none}

Thank you for registering!


=
{drawer:kick}
`;

    printer.print(receipt).catch((error: Error) => {
      toast.error(ts('printer_reported_error', error.message));
    });

    reload();
  }, [cashierSettings, longName, venue, profile, currency, reload, ts]);

  return (
    <Card className="mb-10">
      <CardBody className={`text-center cashier-current-status state-${profile.status}`}>
        <div className="cashier-status-header">
          <MaterialIcon large name={icon} type={color} />
          <div>
            <h4>
              <strong>{ts(text)}</strong>
            </h4>
            {explanation && <p>{ts(explanation)}</p>}
          </div>
        </div>
        <div className="text-start">
          {profile.status === 'complete' ? (
            <>
              <p>{ts('badge_has_been_queued_to')}</p>
              <p>
                <RichMarkdownIt
                  source={ts('pressescto_return_to_the_user_search_page_or_press_space')}
                />
              </p>
            </>
          ) : (
            <>
              <CashierMessages messages={profile.errors} title="errors" type="error" />
              <CashierMessages
                licenseWarnings={profile.licenseWarnings}
                messages={profile.warnings}
                openUpdateFromScannerModal={openUpdateFromScannerModal}
                title="warnings"
                type="warning"
              />
              <CashierMessages messages={profile.info} title="info" type="info" />
              <NotesList notes={profile.notes} />
            </>
          )}
          {profile.registration && (
            <RegistrationOptionList
              regOptions={profile.regOptions}
              registration={profile.registration}
            />
          )}
        </div>
      </CardBody>
      <CardFooter>
        {profile.dueBalance > 0 &&
          (cashierSettings.config.enablePayments ? (
            <PaymentButtons
              dueBalance={profile.dueBalance}
              receiptAndReload={receiptAndReload}
              reload={reload}
              userId={profile.user.id}
            />
          ) : (
            <CashierPaymentsNotEnabled />
          ))}
        {profile.dueBalance === 0 && <ActionButtons profile={profile} reload={reload} />}
      </CardFooter>
    </Card>
  );
};

interface UndeliveredProductsListProps {
  readonly userId: number;
  readonly products: UndeliveredProducts[];
  readonly refresh: () => void;
}

export const UndeliveredProductsList: FC<UndeliveredProductsListProps> = ({
  userId,
  products,
  refresh,
}) => {
  const { ts } = useTranslation();
  const cashierSettings = useContext(CashierContext);
  const [selected, setSelected] = useState<number[]>([]);
  const onSuccess = useCallback(() => {
    refresh();
    setSelected([]);
  }, [refresh, setSelected]);

  const [receiptPrinter, setReceiptPrinter] = useState<ReceiptPrinterBase | undefined>();

  useEffect(() => {
    const updateReceiptPrinter = () => {
      const printer = getReceiptPrinter(cashierSettings);
      setReceiptPrinter(printer);
    };

    for (const m of cashierSettings.receiptPrinterManagers) {
      m.addEventListener('connectedDevice', updateReceiptPrinter);
      m.addEventListener('disconnectedDevice', updateReceiptPrinter);
    }

    updateReceiptPrinter();

    return () => {
      for (const m of cashierSettings.receiptPrinterManagers) {
        m.removeEventListener('connectedDevice', updateReceiptPrinter);
        m.removeEventListener('disconnectedDevice', updateReceiptPrinter);
      }
    };
  }, [cashierSettings]);

  const printPickList = useCallback(() => {
    if (receiptPrinter === undefined || products.length === 0) {
      return;
    }

    // TODO make this dynamic and move it someplace better.

    // ReceiptLine formatted document
    const receipt = `
^^^PICK LIST
^^^Badge # ${userId}

{width:4 *}
QTY| ITEM |
-
${products
  .filter((p) => !p.dispatchedOn)
  .map((p) => `${p.quantity} |${displayName(p.product)}`)
  .join('\n\n')}


=
`;

    receiptPrinter.print(receipt).catch((error: Error) => {
      toast.error(ts('printer_reported_error', error.message));
    });
  }, [receiptPrinter, products, userId, ts]);

  if (products.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardBody>
        <div className="cashier-status-header">
          <div>
            <h4>
              <strong>{ts('undelivered_products')}</strong>
            </h4>
          </div>
        </div>
        <FulfillmentList items={products} selected={selected} setSelected={setSelected} />
      </CardBody>
      <CardFooter>
        {receiptPrinter && (
          <Button
            color="primary"
            key="printPickList"
            onClick={printPickList}
            style={{ float: 'left' }}
          >
            {ts('print_pick_list')}
          </Button>
        )}
        <ActionButton
          action={async () => {
            await api.setOrderItemsAsFulfilled(userId, selected);
          }}
          color="success"
          disabled={selected.length === 0}
          onSuccess={onSuccess}
          style={{ float: 'right' }}
        >
          {ts('mark_as_delivered')}
        </ActionButton>
      </CardFooter>
    </Card>
  );
};

interface RegistrationOptionListProps {
  readonly registration: UserRegistration;
  readonly regOptions: RegistrationOptionInput[];
}

const RegistrationOptionList: FC<RegistrationOptionListProps> = ({ registration, regOptions }) => {
  const { ts } = useTranslation();
  const options = Object.entries(registration.options).filter(([key]) =>
    regOptions.some((t) => t.id.toString() === key),
  );

  if (options.length === 0) {
    return null;
  }

  return (
    <div>
      <p style={{ marginBottom: 0 }}>
        {registration.paidOrderItem
          ? displayName(registration.paidOrderItem)
          : displayName(registration.attendanceType)}{' '}
        {ts('options')}
      </p>
      <ul>
        {options.map(([key, value]) => {
          const option = regOptions.find((t) => t.id.toString() === key);

          if (!option) {
            return null;
          }

          return (
            <li key={key}>
              <strong>{option.name}</strong>: {value as OptionDataValuePrint}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const CashierPaymentsNotEnabled: FC = () => {
  const { ts } = useTranslation();
  return (
    <div className="cashier-payment-notenabled">
      <MaterialIcon name="front_hand" type="danger" />
      <div>
        <div>
          <strong>{ts('station_no_payments')}</strong>
        </div>
        <div>{ts('cashier.please_direct_the_attendee_to')}</div>
      </div>
    </div>
  );
};

interface PaymentButtonsProps {
  readonly dueBalance: number;
  readonly userId: number;
  readonly receiptAndReload: () => void;
  readonly reload: () => void;
}

const PaymentButtons: FC<PaymentButtonsProps> = ({
  userId,
  receiptAndReload,
  reload,
  dueBalance,
}) => {
  const { ts, currency } = useTranslation();
  return (
    <>
      <h4 className="text-center mb-10">
        {ts('payment_of_ctousdstrprefduebalance_required', currency(dueBalance))}
      </h4>
      <ActionButton
        action={async () => {
          await api.payWithCash('none', userId);
        }}
        block
        className="mb-10"
        color="primary"
        id="cashPayment"
        onSuccess={receiptAndReload}
      >
        <MaterialIcon name="local_atm" /> {ts('cash_payment')}
      </ActionButton>
      <ActionButton
        action={async () => {
          await api.payWithCard('none', userId);
        }}
        block
        color="primary"
        id="cardPayment"
        onSuccess={reload}
      >
        <MaterialIcon name="credit_card" /> {ts('card_payment')}
      </ActionButton>
    </>
  );
};

interface ActionButtonsProps {
  readonly profile: CashierCheck;
  readonly reload: () => void;
}

const ActionButtons: FC<ActionButtonsProps> = ({ profile, reload }) => {
  const { ts } = useTranslation();
  const cashierSettings = useContext(CashierContext);
  // const { enableNfcBadges } = useConvention();
  const { badge, errors, registration, status, warnings, cashierCheckIn } = profile;

  const handleNfcWarningYes = useCallback(() => {
    cashierSettings.dispatch({
      type: 'NFC_HANDLE_WARNING',
      status: true,
      source: 'nfc',
    });
  }, [cashierSettings]);

  const handleNfcWarningNo = useCallback(() => {
    cashierSettings.dispatch({
      type: 'NFC_HANDLE_WARNING',
      status: false,
      source: 'nfc',
    });
  }, [cashierSettings]);

  if (
    !cashierSettings.config.enablePrintingStaffBadges &&
    warnings.some((c) => c.code === CashierWarning.Volunteer)
  ) {
    return (
      <div className="cashier-volunteer-notenabled">
        <MaterialIcon name="front_hand" style={{ float: 'left' }} type="danger" />
        <div>
          <div>
            <strong>{ts('cashier.this_registration_station_is_not_volunteer')}</strong>
          </div>
          <div>{ts('cashier.please_direct_the_attendee_to')}</div>
        </div>
      </div>
    );
  }

  if (
    registration?.paidOrderItem &&
    cashierSettings.config.checkinProducts !== undefined &&
    !cashierSettings.config.checkinProducts.includes(registration.paidOrderItem.productId)
  ) {
    return (
      <div className="cashier-product-notenabled">
        <MaterialIcon name="front_hand" style={{ float: 'left' }} type="danger" />
        <div>
          <div>
            <strong>
              {ts(
                'cashier.this_registration_station_is_not_product',
                registration.paidOrderItem.name,
              )}
            </strong>
          </div>
          <div>{ts('cashier.please_direct_the_attendee_to')}</div>
        </div>
      </div>
    );
  }

  if (!badge) {
    if (cashierCheckIn) {
      return (
        <Row className="cashier-action-buttons">
          <Col lg={12}>
            <CheckInButton reg={registration} reload={reload} />
          </Col>
        </Row>
      );
    }

    return (
      <div className="cashier-idscan-error">
        <MaterialIcon name="badge" type="warning" />
        <div>
          <div>
            <strong>{ts('cashier_ui.badge_not_configured')}</strong>
          </div>
          <div>{ts('cashier_ui.please_contact_admin')}</div>
        </div>
      </div>
    );
  }

  if (status === 'error' && badge.extraLabels.length === 0) {
    const wasNoBarcodeScanned = errors.some((e) => e.code === CashierError.NoBarcodeScanned);
    if (wasNoBarcodeScanned) {
      return (
        <div className="cashier-idscan-error">
          <MaterialIcon name="front_hand" type="danger" />
          <div>
            <div>
              <strong>{ts('this_registration_station_requires_an')}</strong>
            </div>
            <div>{ts('please_return_to_the_search')}</div>
          </div>
        </div>
      );
    }
  }

  return (
    <Row className="cashier-action-buttons">
      {!cashierCheckIn && (
        <Col lg={12}>
          <OSPrinterButton
            badge={badge}
            hotkey={' '}
            isReprint={!!registration?.badgePrinted}
            requireExtraConfirmation={status !== 'ok'}
          />
          {cashierSettings.state.nfcScanner && (
            <NfcPrinterButton badge={badge} isReprint={!!registration?.badgePrinted} />
          )}
        </Col>
      )}
      {cashierCheckIn && (
        <Col lg={12}>
          <CheckInButton reg={registration} reload={reload} />
        </Col>
      )}
      <Modal isOpen={cashierSettings.state.nfcScanner?.showNfcWarning} toggle={handleNfcWarningNo}>
        <ModalHeader toggle={handleNfcWarningNo}>{ts('warning')}</ModalHeader>
        <ModalBody>{ts('cashier_settings.nfc_modal_reprogram_warning')}</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleNfcWarningNo}>
            {ts('common.no')}
          </Button>
          <Button color="primary" onClick={handleNfcWarningYes}>
            {ts('common.yes')}
          </Button>
        </ModalFooter>
      </Modal>
    </Row>
  );
};

interface CheckInButtonProps {
  readonly reg?: UserRegistration;
  readonly reload: () => void;
}

const CheckInButton: FC<CheckInButtonProps> = ({ reg, reload }) => {
  const { ts } = useTranslation();
  const [loading, setLoading] = useState(false);
  const alreadyChecked = !!reg?.paidOrderItem?.dispatchDate;

  const checkInBadge = useCallback(async () => {
    setLoading(true);
    const barcode = new URLSearchParams(location.search).get('barcode') ?? undefined;

    try {
      await api.markRegistrationBadgeAsPrinted(reg!.userId, !alreadyChecked, barcode);
      reload();
    } catch (error) {
      if (isLogicError(error, LogicError.AttendeeHasErrors)) {
        toast.error(
          ts(
            'registration_has_issues',
            (error.apiResponse.errors.logic[0]!.args!['errors'] as string[]).join(', '),
          ),
        );
      }
    }

    setLoading(false);
  }, [alreadyChecked, reg, reload, ts]);

  return (
    <Col lg={12}>
      <Button
        actionText="print_badge"
        block
        className={alreadyChecked ? 'checkedIn' : 'notCheckedIn'}
        color={alreadyChecked ? 'danger' : 'primary'}
        disabled={loading || !reg}
        id="checkIn"
        onClick={checkInBadge}
        outline={alreadyChecked}
      >
        {ts(alreadyChecked ? 'remove_checkin' : 'checkin_registration')}
      </Button>
    </Col>
  );
};
